import { useState } from "react";
import { Loader2, Package, Truck, CheckCircle2, XCircle, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/Field";
import { api, fmtINR } from "@/lib/api";
import { ReceiptButton } from "@/components/ReceiptButton";
import { useLang } from "@/context/LanguageContext";

const STEPS = ["processing", "shipped", "delivered"];
const LABEL = { new: ["Order received", "ऑर्डर प्राप्त"], processing: ["Packing", "पैक हो रहा है"], shipped: ["Shipped", "भेज दिया गया"], delivered: ["Delivered", "डिलीवर हो गया"], cancelled: ["Cancelled", "रद्द"] };
const ICON = { processing: Package, shipped: Truck, delivered: CheckCircle2 };

const Timeline = ({ status }) => {
  const { L } = useLang();
  if (status === "cancelled") return <div className="flex items-center gap-2 text-red-600 font-bold" data-testid="track-cancelled"><XCircle className="w-5 h-5" />{L(...LABEL.cancelled)}</div>;
  const idx = STEPS.indexOf(status);
  return (
    <div className="grid grid-cols-3 gap-2" data-testid="track-timeline">
      {STEPS.map((s, i) => {
        const Icon = ICON[s];
        const done = i <= idx;
        return (
          <div key={s} className={`p-3 rounded-xl border-2 text-center ${done ? "border-[#FF2E63] bg-[#FF2E63]/10 text-white" : "border-slate-200 text-slate-500"}`} data-testid={`track-step-${s}`}>
            <Icon className="w-6 h-6 mx-auto" />
            <div className="text-xs font-bold mt-1">{L(...LABEL[s])}</div>
          </div>
        );
      })}
    </div>
  );
};

export default function TrackOrder() {
  const { L } = useLang();
  const [f, setF] = useState({ order_no: "", email: "" });
  const [o, setO] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr(""); setO(null);
    try {
      const { data } = await api.post("/orders/track", f);
      setO(data);
    } catch (e2) {
      const d = e2.response?.data?.detail;
      setErr(typeof d === "string" ? d : L("Please check the order number and email.", "कृपया ऑर्डर नंबर और ईमेल जाँचें।"));
    } finally { setLoading(false); }
  };

  const status = o?.fulfilment_status || "new";

  return (
    <div className="bg-[#E8F0FE] min-h-[75vh]" data-testid="track-order-page">
      <div className="max-w-2xl mx-auto px-5 py-16">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF2E63]">{L("Roboshop", "रोबोशॉप")}</span>
        <h1 className="text-4xl font-extrabold text-[#0A192F] mt-2">{L("Track your order", "अपना ऑर्डर ट्रैक करें")}</h1>
        <p className="text-slate-600 mt-2">{L("Enter the order number from your receipt email and the email you used at checkout.", "रसीद ईमेल में दिया ऑर्डर नंबर और चेकआउट वाला ईमेल डालें।")}</p>
        <form onSubmit={submit} className="mt-8 bg-white p-6 rounded-2xl border border-slate-200 grid sm:grid-cols-2 gap-4" data-testid="track-form">
          <Field id="order_no" label={L("Order number", "ऑर्डर नंबर")} required placeholder="e.g. D275E6CF" value={f.order_no} onChange={(e) => setF({ ...f, order_no: e.target.value })} />
          <Field id="track_email" label={L("Email", "ईमेल")} type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
          <Button type="submit" disabled={loading} className="sm:col-span-2 rs-cta h-11 rounded-full" data-testid="track-submit-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : L("Track order", "ट्रैक करें")}
          </Button>
          {err && <p className="sm:col-span-2 text-sm text-red-600" data-testid="track-error">{err}</p>}
        </form>

        {o && (
          <div className="mt-8 bg-white p-6 rounded-2xl border border-slate-200 space-y-5" data-testid="track-result">
            <div className="flex justify-between flex-wrap gap-2">
              <div><div className="text-xs text-slate-500">{L("Order no.", "ऑर्डर नं.")}</div><div className="font-extrabold text-lg" data-testid="track-order-no">{o.order_no}</div></div>
              <div className="text-right"><div className="text-xs text-slate-500">{L("Status", "स्थिति")}</div><div className="font-extrabold text-lg text-[#FF2E63]" data-testid="track-status">{L(...LABEL[status])}</div></div>
            </div>
            {o.payment_status === "paid" && <ReceiptButton path="/receipts/order" body={f} testid="track-receipt-btn" />}
            {o.payment_status !== "paid" && <p className="text-sm text-amber-700 bg-amber-500/15 p-3 rounded-lg flex gap-2" data-testid="track-unpaid"><Clock className="w-4 h-4 mt-0.5" />{L("Payment not completed for this order yet.", "इस ऑर्डर का भुगतान अभी पूरा नहीं हुआ है।")}</p>}
            <Timeline status={status} />
            {(o.courier || o.tracking_number) && (
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="p-3 bg-[#E8F0FE] rounded-lg"><div className="text-xs text-slate-500">{L("Courier", "कूरियर")}</div><div className="font-bold" data-testid="track-courier">{o.courier || "—"}</div></div>
                <div className="p-3 bg-[#E8F0FE] rounded-lg"><div className="text-xs text-slate-500">{L("Tracking number", "ट्रैकिंग नंबर")}</div><div className="font-bold" data-testid="track-number">{o.tracking_number || "—"}</div></div>
              </div>
            )}
            <ul className="divide-y text-sm">
              {o.items.map((i) => <li key={i.item_id} className="py-2 flex justify-between"><span>{i.name} × {i.qty}</span><span>{fmtINR(i.unit_price_inr * i.qty)}</span></li>)}
              {o.gst_inr > 0 && <li className="py-2 flex justify-between text-sm text-slate-600" data-testid="track-gst"><span>GST @ 18%</span><span>+ {fmtINR(o.gst_inr)}</span></li>}
              <li className="py-2 flex justify-between font-bold"><span>{L("Total", "कुल")}</span><span>{fmtINR(o.total_inr)}</span></li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
